import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  onSnapshot,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";

// ─── CHECK IN TO VENUE ────────────────────────────────────────────────────────
export async function checkInToVenue(venueId, user) {
  if (user.isGuest) return;
  const presenceRef = doc(db, "venues", venueId, "presence", user.uid);
  await setDoc(presenceRef, {
    uid: user.uid,
    displayName: user.displayName || "",
    photoURL: user.photoURL || "",
    privacy: user.privacy || "public",
    checkedInAt: serverTimestamp(),
    lastActive: serverTimestamp(),
  });
}

// ─── CHECK OUT OF VENUE ───────────────────────────────────────────────────────
export async function checkOutOfVenue(venueId, uid) {
  await deleteDoc(doc(db, "venues", venueId, "presence", uid));
}

// ─── UPDATE LAST ACTIVE ───────────────────────────────────────────────────────
export async function updateLastActive(venueId, uid) {
  const presenceRef = doc(db, "venues", venueId, "presence", uid);
  await setDoc(presenceRef, { lastActive: serverTimestamp() }, { merge: true });
}

// ─── SUBSCRIBE TO ACTIVE USERS ────────────────────────────────────────────────
// Everyone's browser refreshes its own presence record every minute. A record
// that hasn't been refreshed for a few minutes belongs to someone who closed the
// tab, lost signal or went to sleep, so it is hidden from the list (and cleaned
// up much later). Ages are measured against the viewer's own record, which both
// come from the server's clock, so a wrong clock on someone's phone can't make
// everyone look gone.
export const PRESENCE_REFRESH_MS = 60 * 1000;
const STALE_AFTER_MS = 3 * 60 * 1000;
const DELETE_AFTER_MS = 10 * 60 * 1000;

export function subscribeToActiveUsers(venueId, callback, getSelfUid = () => null) {
  let docs = [];

  const emit = () => {
    const selfUid = getSelfUid();
    const own = docs.find((d) => d.uid === selfUid);
    const reference = own?.lastActiveMs ?? Date.now();

    const age = (d) => (d.lastActiveMs == null ? 0 : reference - d.lastActiveMs);
    callback(docs.filter((d) => d.privacy === "public" && age(d) <= STALE_AFTER_MS));

    // Tidy up records left behind by closed tabs (best effort, a few at a time)
    docs
      .filter((d) => d.uid !== selfUid && age(d) > DELETE_AFTER_MS)
      .slice(0, 5)
      .forEach((d) => checkOutOfVenue(venueId, d.uid).catch(() => {}));
  };

  const unsubscribe = onSnapshot(collection(db, "venues", venueId, "presence"), (snap) => {
    docs = snap.docs.map((d) => {
      const data = d.data();
      return { uid: d.id, ...data, lastActiveMs: data.lastActive?.toMillis?.() ?? null };
    });
    emit();
  });

  // Nothing changes in the database when someone just disappears, so re-check
  // the list on a timer as well
  const timer = setInterval(emit, 30 * 1000);
  return () => { unsubscribe(); clearInterval(timer); };
}

// ─── GET VENUE ────────────────────────────────────────────────────────────────
export async function getVenue(venueId) {
  const snap = await getDoc(doc(db, "venues", venueId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// ─── CREATE VENUE ─────────────────────────────────────────────────────────────
export async function createVenue(venueData) {
  const venueId = venueData.slug || venueData.name.toLowerCase().replace(/\s+/g, "-");
  await setDoc(doc(db, "venues", venueId), {
    ...venueData,
    id: venueId,
    createdAt: serverTimestamp(),
    isActive: true,
    settings: {
      genreFilter: null,
      explicitContent: true,
      requestCooldown: 10,
      maxQueueSize: 50,
      platform: "spotify",
      venuePassword: venueData.venuePassword || "venue2024",
      adminPassword: venueData.adminPassword || "tapped2024",
    },
    vibeProfiles: [
      {
        id: "default",
        name: "Default",
        icon: "🎵",
        genreFilter: null,
        explicitContent: true,
        requestCooldown: 10,
      },
    ],
    activeVibeProfile: "default",
  });
  return venueId;
}

// ─── UPDATE VENUE SETTINGS ────────────────────────────────────────────────────
export async function updateVenueSettings(venueId, settings) {
  await updateDoc(doc(db, "venues", venueId), { settings });
}
